import { geoJSON, LatLng, circleMarker, LatLngExpression } from 'leaflet';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMap, Popup } from 'react-leaflet';

import { useAsyncEffect } from '../../../hooks/useAsyncEffect';

import { getCentroid } from './getCentroid';
import { Cfg, useGISFetcher } from './useGISFetcher';

export type DetailsComponentProps<Details> = {
  details: Details;
} & Pick<Cfg, 'onErr'>;

type Layer<type, extend> = {
  type: type;
  components: {
    DetailsComponent: React.ComponentType<DetailsComponentProps<any>>;
  };
} & extend;

export type FeatureLayerConfig = Layer<
  'feature',
  {
    url: string;
    token?: string;
    query?: Record<string, string>;
    options?: {
      centroidZoom?: number;
    };
  }
>;

export type GISPortalLayerProps = {
  token?: string;
  config: FeatureLayerConfig;
};

export const GISPortalLayer = React.memo<
  GISPortalLayerProps & {
    onSelect: (value: any) => void;
  } & Pick<Cfg, 'onErr'>
>(function ({ token, config, onSelect, onErr }) {
  const layerRef = useRef<any>(null);
  const map = useMap();
  const { loadList } = useGISFetcher({ baseUrl: config.url, token, onErr });

  const [selectedPoint, setSelectedPoint] = useState<Nullable<LatLngExpression>>(null);
  const [details, setDetails] = useState(null);

  const closePopup = useCallback(() => {
    setSelectedPoint(null);
    setDetails(null);
  }, []);

  useEffect(() => {
    onSelect(details);
  }, [details]);

  // TODO: add clearing all points from map
  const loadLayer = async (): Promise<void> => {
    closePopup();

    const data = await loadList(config.query);
    const newLayer = geoJSON(data.list, {
      pointToLayer(_, latlng: LatLng): any {
        const marker = circleMarker(latlng, {
          color: '#2e2e40', // TODO: move to css-variables
          weight: 2,
          stroke: true,
          radius: 12,
          fillColor: '#6a6acf', // TODO: move to css-variables
          fillOpacity: 1,
        });

        marker.on('click', async function (event) {
          setDetails(event.sourceTarget?.feature?.properties);

          setSelectedPoint(event.latlng);
        });

        return marker;
      },
    });
    layerRef.current && layerRef.current.removeFrom(map);
    newLayer.addTo(map);
    layerRef.current = newLayer;

    !!data.list?.features?.length && map.flyTo(getCentroid(data.extent), config.options?.centroidZoom);
    map.on('popupclose', closePopup);
  };

  useAsyncEffect(loadLayer, [token, config.query]);

  if (!selectedPoint) {
    return null;
  }

  return (
    <Popup position={selectedPoint} closeOnEscapeKey closeButton keepInView interactive>
      {details && <config.components.DetailsComponent details={details} />}
    </Popup>
  );
});
